import React, { FC, ReactElement, useEffect, useState } from 'react';
import styled from 'styled-components';
import isArray from 'lodash/isArray';
import NoElementsFound, { NO_ELEMENTS_LC_AUTHORS } from '../../../shared/components/NoElementsFound';
import { DataTable } from '../../../shared/common/DataTable';
import BasicModalLayout from '../../../shared/components/modal/BasicModalLayout';
import { Button, ButtonSize, ButtonTheme } from '../../../shared/common/Button';
// TODO: these styles directly modify the modal; cleanup modal
import './AuthorListPopup.scss';
import { PerformerOut } from '../../../store/api';
import { CountryOut } from '../../../store/apiLibx';
import { getCountries } from '../performer/LCCountries';
import { useTranslation } from 'react-i18next';

// This is to use ellipsis overflow in a variable width table cell
// left and right are adjusted to match the padding of other cells in the table
// top and transform are set to vertically align the text middle
const StBiographyCell = styled.span`
    position: absolute;
    left: 5px;
    right: 5px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    top: 50%;
    transform: translateY(-50%);
`;

const StTable = styled(DataTable)`
    font-size: 14px;
`;

interface Props {
    performers?: PerformerOut[];
    selectedPerformers?: PerformerOut[];
    countries?: CountryOut[];
    onAbort: () => void;
    onAddPerformer?: (performer: PerformerOut) => void;
    onCreatePerformer?: () => void;
    show: boolean;
}

export const AuthorListPopup: FC<Props> = ({
    countries = getCountries(),
    selectedPerformers = [],
    performers,
    onAddPerformer = () => {},
    onAbort,
    show,
    onCreatePerformer = () => {}
}) => {
    const { t } = useTranslation();
    const [bodyHeight, setBodyHeight] = useState<number | null>(null);

    useEffect(() => {
        window.addEventListener('resize', handleResize, false);
        handleResize();
        return () => {
            window.removeEventListener('resize', handleResize, false);
        };
    }, []);

    function handleResize() {
        const bodyHeight = document.body.getBoundingClientRect().height;
        setBodyHeight(bodyHeight);
    }

    const tableMaxHeight = Math.floor((bodyHeight ?? 0) * 0.8 - 250);
    const colBiographyWidth = '40%';
    const colCountriesWidth = '17.5%';
    const colButtonWidth = '9%';
    let performerTable: ReactElement;

    if (performers && isArray(performers) && performers.length > 0 && countries && countries?.length > 0) {
        performerTable = (
            <StTable
                cellPaddingVertical={3}
                cellPaddingHorizontal={5}
                data={performers as any}
                minHeight={tableMaxHeight}
                maxHeight={tableMaxHeight}
                getRowStyle={p => ({ fontWeight: selectedPerformers.includes(p) ? 'bold' : undefined })}
                columns={[
                    {
                        label: t('yourLocalContent.yourAuthors.lastName'),
                        accessor: 'lastName'
                    },
                    {
                        label: t('yourLocalContent.yourAuthors.firstName'),
                        accessor: 'firstName'
                    },
                    {
                        label: t('yourLocalContent.yourAuthors.country'),
                        accessor: p => t(`countries:countries.${p?.country}`, { defaultValue: '' }),
                        style: {
                            width: colCountriesWidth
                        }
                    },
                    {
                        label: t('yourLocalContent.yourAuthors.biography'),
                        accessor: p => <StBiographyCell>{p.biography}</StBiographyCell>,
                        style: {
                            width: colBiographyWidth,
                            position: 'relative'
                        }
                    },
                    {
                        label: '',
                        accessor: p => (
                            <Button
                                text={
                                    selectedPerformers.includes(p)
                                        ? t('yourLocalContent.yourAuthors.added')
                                        : t('yourLocalContent.yourAuthors.add')
                                }
                                disabled={selectedPerformers.includes(p)}
                                onClick={() => onAddPerformer(p)}
                                size={ButtonSize.LARGE}
                                className="add-btn"
                            />
                        ),
                        style: {
                            width: colButtonWidth
                        }
                    }
                ]}
            />
        );
    } else {
        performerTable = (
            <div style={{ height: tableMaxHeight }}>
                <NoElementsFound type={NO_ELEMENTS_LC_AUTHORS} />
            </div>
        );
    }

    const cancelButton = (
        <Button text={t('cancel')} onClick={onAbort} size={ButtonSize.LARGE} theme={ButtonTheme.POSITIVE_INVERT} />
    );

    const okButton = (
        <Button
            text={t('yourLocalContent.yourAuthors.createNewAuthor')}
            onClick={onCreatePerformer}
            size={ButtonSize.LARGE}
        />
    );

    const header = <h4 style={{ fontSize: '22px' }}>{t('yourLocalContent.yourTitles.addAuthor')}</h4>;

    return (
        <BasicModalLayout
            visible={show}
            onClose={onAbort}
            header={header}
            content={performerTable}
            okButton={okButton}
            cancelButton={cancelButton}
            width={1024}
        />
    );
};
